import { DeleteSquareIcon, PlusIcon, SuccessIcon } from '@/assets/icons';
import { DocumentTypeEnum, OPTION_SELECT, PageTypeEnum } from '@/constant';
import { IAutocompleteSuggestionItem } from '@/services';
import {
	CommonSelectors,
	DefineDocumentAreaActions,
	DefineDocumentAreaSelectors,
} from '@/stores';
import { FRANCHISE_EXCLUDED_DOCUMENT_TYPES } from '@/stores/define-document-upload';
import { cls, delay, filter, insertObjectIf, map, randomId } from '@/utils';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormikProps } from 'formik';
import { includes } from 'ramda';
import { isNilOrEmpty, isNotNilOrEmpty } from 'ramda-adjunct';

import { Select } from '..';
import EditActions from '../common/edit-actions';
import { getRelativeOptionsByDocumentType } from './define-document-area.config';

type FormValues = {
	id: string;
	mappingList: Array<{
		id: number;
		refTableName?: PageTypeEnum;
		refTableId?: string;
	}>;
	refTableName?: PageTypeEnum;
	refTableId?: string;
};

type RelationDocumentProps = {
	form: FormikProps<FormValues>;
	record: {
		id: number;
		mappingList: Array<{
			id: number;
			refTableName?: PageTypeEnum;
			refTableId?: string;
		}>;
		documentType: DocumentTypeEnum;
		refTableName?: PageTypeEnum;
	};
	pageType: PageTypeEnum;
	toggleEdit(
		e?: React.MouseEvent<HTMLDivElement>,
		getAdditionalValues?: (value: string, prevValues: FormValues) => void
	): void;
	editing: boolean;
};

type MappingItem = {
	id: number;
	refTableName?: PageTypeEnum;
	refTableId?: string;
	fileId?: string;
	status?: 'NEW';
};

const RelationDocument = ({
	form,
	pageType,
	editing,
	toggleEdit,
	record,
}: RelationDocumentProps) => {
	const { values, setValues } = form;
	const dispatch = useDispatch();
	const [showSuccess, setShowSuccess] = useState(false);
	const autocompleteSuggestion = useSelector(
		CommonSelectors.selectAutocompleteSuggestion
	);

	const paramsGetDocumentList = useSelector(
		DefineDocumentAreaSelectors.selectParams
	);

	const getAutocompleteOptions = useCallback(
		(refTableName?: PageTypeEnum) => {
			let list: IAutocompleteSuggestionItem[] = [];

			switch (refTableName) {
				case PageTypeEnum.FRANCHISE:
					list = autocompleteSuggestion.franchises || [];
					break;

				case PageTypeEnum.APPLICANT:
					list = autocompleteSuggestion.applicants || [];
					break;

				case PageTypeEnum.RECOMMENDER:
					list = autocompleteSuggestion.recommenders || [];
					break;

				case PageTypeEnum.LODGING:
					list = autocompleteSuggestion.lodgings || [];
					break;

				default:
					list = [];
			}

			list = list.filter((item) => {
				const isSameId = Number(item?.id) === Number(values?.refTableId);
				const isSameTableName = values.refTableName === refTableName;
				return !(isSameId && isSameTableName);
			});

			return map(list, (item) => ({ value: item?.id, label: item?.value }));
		},
		[autocompleteSuggestion, values]
	);

	const relatedInfoOptions = useMemo(
		() => [
			OPTION_SELECT,
			...getRelativeOptionsByDocumentType(record?.documentType),
		],
		[record?.documentType]
	);

	const isExcludedDocType = useMemo(() => {
		return (
			record?.refTableName === PageTypeEnum.FRANCHISE &&
			FRANCHISE_EXCLUDED_DOCUMENT_TYPES.includes(record?.documentType)
		);
	}, [record?.refTableName, record?.documentType]);

	// Determine if we can add more relation rows based on remaining unused types
	const canAddMore = useMemo(() => {
		const usedTypes = (values?.mappingList || [])
			.map((ml: MappingItem) => ml.refTableName)
			.filter((v: any) => isNotNilOrEmpty(v));

		const usedTypesNormalized = isExcludedDocType
			? usedTypes.filter((t: any) => t !== PageTypeEnum.APPLICANT)
			: usedTypes;

		const remaining = relatedInfoOptions.filter((opt) => {
			if (isNilOrEmpty(opt?.value)) return false;
			if (opt?.value === values?.refTableName) {
				if (isExcludedDocType && opt?.value === PageTypeEnum.APPLICANT) {
					return true;
				}
				return false;
			}

			return !usedTypesNormalized.includes(opt?.value as any);
		});

		return remaining.length > 0;
	}, [values, isExcludedDocType, relatedInfoOptions]);

	const relationDocumentList = useMemo(
		() =>
			filter(
				values?.mappingList,
				(relationDoc) =>
					includes(
						relationDoc?.refTableName,
						relatedInfoOptions.map((item) => item.value)
					) &&
					(relationDoc?.refTableName !== values.refTableName ||
						Number(relationDoc?.refTableId) !== Number(values.refTableId))
			),
		[values, relatedInfoOptions]
	);

	const getFilteredOptions = useCallback(
		(mappingItem: MappingItem, index: number) => {
			const autocompleteOptions = getAutocompleteOptions(
				mappingItem?.refTableName
			);

			return autocompleteOptions.filter(
				(relation) =>
					!(
						Number(relation.value) === Number(values?.refTableId) &&
						mappingItem?.refTableName === values?.refTableName
					) &&
					!relationDocumentList.some(
						(valueItem, valueIndex) =>
							valueIndex !== index &&
							Number(relation.value) === Number(valueItem.refTableId)
					)
			);
		},
		[values, getAutocompleteOptions, relationDocumentList]
	);

	const onAddRowRelation = useCallback(
		(index: number) => {
			setValues((prevValues) => {
				const mappingList = [...(prevValues.mappingList || [])];
				const newMapping = {
					id: randomId(),
					refTableId: '',
					refTableName: relatedInfoOptions[0]?.value as PageTypeEnum,
					status: 'NEW',
				};
				mappingList.splice(index + 2, 0, newMapping);
				return {
					...prevValues,
					mappingList,
				};
			});
		},
		[relatedInfoOptions]
	);

	const getDefineDocumentAreaList = useCallback(() => {
		dispatch(
			DefineDocumentAreaActions.getDefineDocumentAreaListRequest({
				...paramsGetDocumentList,
				callback: () => {
					toggleEdit();
					setShowSuccess(true);
					delay(3000).then(() => {
						setShowSuccess(false);
					});
				},
			})
		);
	}, [paramsGetDocumentList, toggleEdit]);

	const handleDeleteDocument = useCallback(
		(mappingItem: MappingItem) => {
			dispatch(
				DefineDocumentAreaActions.deleteDocumentsRequest({
					params: {
						refTableName: mappingItem?.refTableName,
						refTableId: mappingItem?.refTableId,
						history: false,
						id: [mappingItem?.fileId || ''],
					},
					callback: (error) => {
						if (error) {
							return;
						}

						getDefineDocumentAreaList();
					},
				})
			);
		},
		[getDefineDocumentAreaList]
	);

	const onSubtractRelation = useCallback(
		(mappingItem: MappingItem) => () => {
			const setFormikValue = () => {
				setValues((prevValues) => {
					return {
						...prevValues,
						mappingList: filter(
							prevValues?.mappingList,
							(item) => item.id !== mappingItem?.id
						),
					};
				});
			};

			if (mappingItem?.status === 'NEW') {
				setFormikValue();
				return;
			}

			handleDeleteDocument(mappingItem);
		},
		[handleDeleteDocument]
	);

	const onChangeRelation = useCallback(
		(mappingItem: MappingItem) => (value: PageTypeEnum) => {
			if (!value && mappingItem?.status !== 'NEW') {
				const mappingItemToDelete = record?.mappingList?.find(
					(item) => item.id === mappingItem?.id
				);

				const mappingItemDelete = {
					...mappingItem,
					refTableId: mappingItemToDelete?.refTableId,
					refTableName: mappingItemToDelete?.refTableName as PageTypeEnum,
				};
				handleDeleteDocument(mappingItemDelete);
				return;
			}

			setValues((prevValues) => {
				return {
					...prevValues,
					mappingList: map(prevValues?.mappingList, (item) =>
						item.id === mappingItem?.id
							? { ...item, refTableName: value, refTableId: undefined }
							: item
					),
				};
			});
		},
		[handleDeleteDocument, record?.mappingList]
	);

	const onChangeValue = useCallback(
		(mappingItem: MappingItem) => (value: string) => {
			dispatch(
				DefineDocumentAreaActions.updateDocumentItemRequest({
					data: {
						id: values?.id,
						'reqGroupRefer.refTableName': mappingItem?.refTableName,
						'reqGroupRefer.refTableId': value,
						...insertObjectIf(mappingItem?.status !== 'NEW', {
							'reqGroupRefer.id': mappingItem?.id,
						}),
					},
					callback: (error) => {
						if (error) {
							return;
						}
						getDefineDocumentAreaList();
					},
				})
			);
		},
		[values, getDefineDocumentAreaList]
	);

	const renderRelationItem = useCallback(
		(item: MappingItem, index: number, list: MappingItem[]) => {
			const _optionsRelation = getFilteredOptions(item, index);

			// Build options for refTableName: exclude types already used by other mapping items
			const usedTypes = (values?.mappingList || [])
				.filter((ml: MappingItem) => ml.id !== item.id)
				.map((ml: MappingItem) => ml.refTableName)
				.filter((v: any) => isNotNilOrEmpty(v));

			const usedTypesNormalized = isExcludedDocType
				? usedTypes.filter((t: any) => t !== PageTypeEnum.APPLICANT)
				: usedTypes;

			const refTableNameOptions = relatedInfoOptions.filter((opt) => {
				if (isNilOrEmpty(opt?.value) || opt?.value === item?.refTableName)
					return true;

				if (opt?.value === values?.refTableName) {
					if (isExcludedDocType && opt?.value === PageTypeEnum.APPLICANT) {
						return true;
					}
					return false;
				}
				return !usedTypesNormalized.includes(opt?.value as any);
			});

			const typeLabel = relatedInfoOptions?.find?.(
				(it) => it?.value === item?.refTableName
			)?.label;
			const valueShow = _optionsRelation?.find?.(
				(it) => it?.value === Number(item?.refTableId)
			)?.label;

			return (
				<div key={item.id}>
					{editing ? (
						<div
							className="flex justify-end items-center gap-2.5"
							key={item.id}
						>
							<div
								className={cls(
									'cursor-pointer',
									!canAddMore && 'opacity-50 pointer-events-none'
								)}
								onClick={() => canAddMore && onAddRowRelation(index)}
							>
								<PlusIcon />
							</div>

							<div className="flex gap-[5px] justify-end">
								<Select
									options={refTableNameOptions}
									value={item?.refTableName}
									className="!w-[100px]"
									onChange={onChangeRelation(item)}
								/>

								<Select
									options={_optionsRelation}
									value={item?.refTableId}
									className="!w-[175px]"
									showSearch
									removeIcon
									suffixIcon
									onChange={onChangeValue(item)}
								/>
							</div>
							{list?.length > 1 && (
								<div
									className="cursor-pointer"
									onClick={onSubtractRelation(item)}
								>
									<DeleteSquareIcon width={12} height={12} />
								</div>
							)}
						</div>
					) : (
						<div className="editable-cell cursor-text row-between gap-x-2">
							<div className="w-full">
								{typeLabel && valueShow ? `${typeLabel}: ${valueShow}` : '-'}
							</div>
						</div>
					)}
				</div>
			);
		},
		[
			getFilteredOptions,
			values,
			editing,
			showSuccess,
			onAddRowRelation,
			onSubtractRelation,
			onChangeRelation,
			onChangeValue,
			isExcludedDocType,
			relatedInfoOptions,
			canAddMore,
		]
	);

	const onClick = useCallback(() => {
		if (editing) return;

		if (!relationDocumentList?.length) {
			setValues((prevValues) => {
				return {
					...prevValues,
					mappingList: [
						{
							id: randomId(),
							refTableId: '',
							refTableName: relatedInfoOptions[0]?.value as PageTypeEnum,
							status: 'NEW',
						},
					],
				};
			});
		}

		toggleEdit();
	}, [editing, toggleEdit, relationDocumentList, relatedInfoOptions]);

	const onCancel = useCallback(() => {
		toggleEdit(undefined, () => {
			return {
				mappingList: record?.mappingList,
			};
		});
	}, [toggleEdit, record?.mappingList]);

	useEffect(() => {
		if (editing && !relationDocumentList?.length) {
			onAddRowRelation(0);
		}
	}, [editing, relationDocumentList]);

	return (
		<div className="flex gap-2 justify-center items-center" onClick={onClick}>
			<div className={cls('flex flex-col gap-1 editable-cell cursor-pointer')}>
				{!editing && !relationDocumentList?.length
					? '-'
					: map(relationDocumentList, renderRelationItem)}
			</div>
			{showSuccess && (
				<span className="ml-auto">
					<SuccessIcon />
				</span>
			)}
			{editing && <EditActions onCancel={onCancel} hiddenSave />}
		</div>
	);
};

export default memo(RelationDocument);
